import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import CustomDialog from './CustomDialog'
import { ArrowEngIcon } from './Svgs'
import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '../store'
import { useSelector } from 'react-redux'
import { useEffect, useState } from 'react'
import ProgressBarComponent from './ProgressBarComponent'
import { LoadingPage } from '../models/File'

const permitFileSelector = createSelector(
  [(state: RootState) => state.permit],
  (permit) => permit.file,
)

interface StampAndApproveProps {
  isOpen: boolean
  handleClose: (showPreviousWindow?: boolean) => void
}

const StampAndApproveComponent: React.FC<StampAndApproveProps> = ({
  isOpen,
  handleClose,
}) => {
  const permitFile = useSelector(permitFileSelector)
  const [selectedPageId, setSelectedPageId] = useState<string | undefined>()
  const [totalPages, setTotalPages] = useState<number>(0)
  const [signedPages, setSignedPages] = useState<LoadingPage[]>([])

  const isPreviousDisabled = () => {
    const selectedPageIndex =
      permitFile?.pages.findIndex((page) => page.id === selectedPageId) ?? -1
    return selectedPageIndex < 1
  }

  const isNextDisabled = () => {
    const selectedPageIndex =
      permitFile?.pages.findIndex((page) => page.id === selectedPageId) ?? -1
    const length = permitFile ? permitFile?.pages.length : 1
    return selectedPageIndex >= length - 1
  }

  const previousItemClickHandle = () => {
    const selectedPageIndex =
      permitFile?.pages.findIndex((page) => page.id === selectedPageId) ?? -1
    if (selectedPageIndex !== -1 && permitFile && selectedPageIndex > 0) {
      setSelectedPageId(permitFile?.pages[selectedPageIndex - 1].id)
    }
  }

  const nextItemClickHandle = () => {
    const selectedPageIndex =
      permitFile?.pages.findIndex((page) => page.id === selectedPageId) ?? -1
    if (
      selectedPageIndex !== -1 &&
      permitFile &&
      selectedPageIndex < permitFile?.pages.length - 1
    ) {
      setSelectedPageId(permitFile?.pages[selectedPageIndex + 1].id)
    }
  }

  const stampHandle = () => {
    const page = permitFile?.pages.find((page) => page.id === selectedPageId)
    if (!permitFile || !page) {
      return
    }

    setSignedPages([...signedPages, page])
  }

  useEffect(() => {
    if (permitFile) {
      setSelectedPageId(permitFile?.pages[0]?.id)
      setTotalPages(permitFile?.pages.length)
    }
  }, [permitFile])

  return (
    <CustomDialog
      isOpen={isOpen}
      title=''
      hideActions={true}
      hideCloseButton={true}
      paperStyle={{
        borderRadius: '0.4rem',
        padding: 0,
        boxShadow: '0px 3px 5px 0px rgba(0, 0, 0, 0.25)',
        width: '89rem',
        maxWidth: '100%',
        background: '#F5F5F5',
      }}
      handleClose={() => handleClose(false)}
    >
      <Stack sx={{ p: '0.75rem', gap: '1rem' }}>
        <Box display='flex' justifyContent='space-between'>
          <Button
            disableRipple
            onClick={() => handleClose(true)}
            startIcon={<ArrowEngIcon color='#04002E' />}
            sx={{
              '& .MuiButton-startIcon': {
                transform: 'rotate(180deg)',
              },
              '&:hover': {
                background: 'transparent',
              },
              color: '#04002E',
              textTransform: 'none',
              fontSize: '1rem',
              lineHeight: 'normal',
            }}
          >
            Stamp & Approve Plans
          </Button>
          <Box display='flex' gap='0.5rem'>
            <Button
              disableRipple
              disabled={isPreviousDisabled()}
              onClick={previousItemClickHandle}
              sx={{
                color: '#fff',
                background: '#04002E',
                textTransform: 'none',
                fontSize: '0.7rem',
                lineHeight: 'normal',
                minWidth: '6rem',
                '&:disabled': {
                  color: '#fff',
                },
              }}
            >
              Previous
            </Button>
            <Button
              disableRipple
              disabled={isNextDisabled()}
              onClick={nextItemClickHandle}
              sx={{
                color: '#fff',
                background: '#04002E',
                textTransform: 'none',
                fontSize: '0.7rem',
                lineHeight: 'normal',
                minWidth: '6rem',
                '&:disabled': {
                  color: '#fff',
                },
              }}
            >
              Next
            </Button>
          </Box>
        </Box>
        <Box display='flex' gap='0.5rem' height='48rem'>
          <Stack
            display='flex'
            flexDirection='column'
            width='16rem'
            height='calc(100% - 1rem)'
            rowGap='0.4rem'
            p='0.5rem'
            sx={{
              background: '#fff',
              borderRadius: '0.4rem',
              overflowY: 'auto',
            }}
          >
            {permitFile &&
              permitFile.pages.map((page, index) =>
                !page.isLoading ? (
                  <Box
                    key={index}
                    display='flex'
                    flex={1}
                    alignItems='center'
                    justifyContent='center'
                    borderRadius='0.2rem'
                    maxWidth='100%'
                    height='auto'
                    border={`1px solid ${page.id === selectedPageId ? '#1705E5' : '#CFCFCF'}`}
                    boxShadow='0px 2.75px 2.75px 0px #00000040'
                    position='relative'
                    onClick={() => setSelectedPageId(page.id)}
                  >
                    <Box
                      component='img'
                      src={page.image}
                      alt='Permit File Page Content'
                      width='100%'
                      height='auto'
                      borderRadius='inherit'
                    />
                    <Box
                      position='absolute'
                      top='0.2rem'
                      left='0.2rem'
                      bgcolor={
                        signedPages.find((signed) => signed.id === page.id)
                          ? '#00C961'
                          : '#97999B'
                      }
                      padding='0.3rem 0.4rem'
                      borderRadius='0.2rem'
                      fontSize='0.64rem'
                    >
                      <Typography
                        variant='caption'
                        sx={{
                          fontSize: '0.64rem',
                          color: signedPages.find(
                            (signed) => signed.id === page.id,
                          )
                            ? '#04002E'
                            : '#fff',
                          lineHeight: 'normal',
                        }}
                      >
                        {signedPages.find((signed) => signed.id === page.id)
                          ? 'Signed'
                          : 'Not Signed'}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    key={index}
                    display='flex'
                    alignItems='center'
                    justifyContent='center'
                    maxWidth='100%'
                    minHeight='5rem'
                    border={`1px solid ${page.id === selectedPageId ? '#1705E5' : '#CFCFCF'}`}
                    boxShadow='0px 2.75px 2.75px 0px #00000040'
                    borderRadius='0.2rem'
                  >
                    <CircularProgress size={40} sx={{ color: '#1705E5' }} />
                  </Box>
                ),
              )}
          </Stack>
          <Stack
            display='flex'
            flexDirection='column'
            flex='1'
            height='99%'
            gap='0.5rem'
            sx={{
              borderRadius: '0.4rem',
              position: 'relative',
            }}
          >
            <Box display='flex' bgcolor='#fff'>
              <Box
                component='img'
                display='flex'
                flex={1}
                alignItems='center'
                justifyContent='center'
                borderRadius='0.2rem'
                maxWidth='100%'
                height='auto'
                src={
                  permitFile?.pages.find((page) => page.id === selectedPageId)
                    ?.image
                }
                alt='Structural Permit Set Page Content'
              />
            </Box>
            <ProgressBarComponent
              completed={signedPages.length}
              total={totalPages}
              height='0.75rem'
              hideOverlay={true}
              hideHeader={true}
            />
            {!!!signedPages.find(
              (signed: LoadingPage) => signed.id === selectedPageId,
            ) && (
              <>
                <Box
                  display='flex'
                  onClick={stampHandle}
                  sx={{
                    position: 'absolute',
                    right: '1rem',
                    top: '8.25rem',
                    width: '4.75rem',
                    height: '4.75rem',
                    background: 'yellow',
                    zIndex: 100,
                    cursor: `url(/images/stamping.png) 0 60, auto`,
                  }}
                ></Box>
                <Box
                  display='flex'
                  sx={{
                    position: 'absolute',
                    right: '6.75rem',
                    top: '10rem',
                    background: '#04002E',
                    color: '#fff',
                    p: '0.5rem',
                    fontSize: '0.76rem',
                    fontWeight: 500,
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      right: '-1.85rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      borderWidth: '0.95rem',
                      borderStyle: 'solid',
                      borderColor:
                        'transparent transparent transparent #04002E',
                    },
                  }}
                >
                  Stamp Here
                </Box>
              </>
            )}
          </Stack>
        </Box>
      </Stack>
    </CustomDialog>
  )
}

export default StampAndApproveComponent
